/**
 * CostSchemePalette — the right-hand sidebar listing draggable placeholders, grouped and searchable.
 * Dragging an item starts a `palette` drag payload the board's drop targets consume; `onItemDragStart` /
 * `onItemDragEnd` let the board light up its drop zones while a drag is in flight. Groups can be collapsed
 * locally, collapsed/expanded all at once, and a live search force-opens every matching group. Each group
 * also exposes a blank box (drag to create a part without a source placeholder). Data (fetch + filter +
 * grouping) comes from {@link usePalettePlaceholders}.
 */
import { useState } from 'react';
import { ChevronsDownUp, ChevronsUpDown, MapPin, Plus, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { PlaceholderListItem } from '@/api/generated/schemas';
import { cn } from '@/shared/lib/utils';
import { TypeIcon, writeDrag, type PalettePayload } from '../model';
import { itemName, paramChips, type PaletteGroup } from './paletteGroups';
import { usePalettePlaceholders } from './usePalettePlaceholders';

interface CostSchemePaletteProps {
  readOnly: boolean;
  /** `false` u nulanočního programu — ubytování se pak vůbec nenabízí (nemá kde vzniknout). */
  allowAccommodation: boolean;
  /** Reports the payload being dragged so the board can light up only the compatible drop zones. */
  onItemDragStart: (payload: PalettePayload) => void;
  onItemDragEnd: () => void;
}

export function CostSchemePalette({
  readOnly,
  allowAccommodation,
  onItemDragStart,
  onItemDragEnd,
}: CostSchemePaletteProps) {
  const { t } = useTranslation('venua-planner');
  const [query, setQuery] = useState('');
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set());

  const { isLoading, filtered, groups } = usePalettePlaceholders(query, t, { allowAccommodation });

  // Groups start collapsed; a live search force-opens every matching group (the manual open state is
  // preserved and restored once the query clears).
  const searching = query.trim() !== '';
  const isOpen = (key: string) => searching || opened.has(key);
  const anyOpen = groups.some((group) => isOpen(group.key));

  const toggleGroup = (key: string) => {
    setOpened((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    setOpened(anyOpen ? new Set() : new Set(groups.map((group) => group.key)));
  };

  const handleDragStart = (event: React.DragEvent, item: PlaceholderListItem) => {
    const payload: PalettePayload = {
      source: 'palette',
      placeholderId: item.id,
      type: item.type,
      transportKind: item.transportKind ?? null,
      name: itemName(item),
    };
    writeDrag(event, payload, 'copy');
    onItemDragStart(payload);
  };

  const handleBlankDragStart = (event: React.DragEvent, group: PaletteGroup) => {
    const payload: PalettePayload = {
      source: 'palette',
      placeholderId: null,
      type: group.type,
      transportKind: group.transportKind,
      name: group.label,
    };
    writeDrag(event, payload, 'copy');
    onItemDragStart(payload);
  };

  return (
    <aside className="tp-palette">
      <div className="tp-palette-head">
        <div className="tp-palette-titlerow">
          <span className="tp-palette-title">{t('costScheme.palette.title')}</span>
          <span className="tp-palette-count">{filtered.length}</span>
        </div>
        <div className="tp-palette-hint">{t('costScheme.palette.hint')}</div>
        <div className="gap-phx-2 mt-2.5 flex items-center">
          <div className={cn('tp-palette-search', 'mt-0! min-w-0 flex-1')}>
            <Search size={13} />
            <input
              type="text"
              placeholder={t('costScheme.palette.searchPlaceholder')}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            {query ? (
              <button
                type="button"
                className="tp-palette-clear text-phx-text-muted absolute right-1.5 inline-flex cursor-pointer border-0 bg-transparent"
                onClick={() => setQuery('')}
                title={t('costScheme.palette.clear')}
                aria-label={t('costScheme.palette.clear')}
              >
                <X size={12} />
              </button>
            ) : null}
          </div>
          <button
            type="button"
            className="rounded-phx border-phx-border bg-phx-bg text-phx-text-muted hover:bg-phx-surface-2 hover:text-phx-text flex h-7 w-7 flex-none items-center justify-center border"
            onClick={toggleAll}
            title={
              anyOpen ? t('costScheme.palette.collapseAll') : t('costScheme.palette.expandAll')
            }
            aria-label={
              anyOpen ? t('costScheme.palette.collapseAll') : t('costScheme.palette.expandAll')
            }
          >
            {anyOpen ? <ChevronsDownUp size={15} /> : <ChevronsUpDown size={15} />}
          </button>
        </div>
      </div>
      <div className="tp-palette-scroll">
        {isLoading ? (
          <div className="tp-palette-empty">{t('costScheme.palette.loading')}</div>
        ) : groups.length === 0 ? (
          <div className="tp-palette-empty">{t('costScheme.palette.empty')}</div>
        ) : (
          groups.map((group) => {
            const open = isOpen(group.key);
            return (
              <div
                key={group.key}
                className="tp-pal-cat"
                style={{ ['--cat-color' as string]: group.color }}
              >
                <button
                  type="button"
                  className="tp-pal-cathead"
                  onClick={() => toggleGroup(group.key)}
                >
                  <span className="tp-pal-catdot" />
                  <span className="tp-pal-catlabel">{group.label}</span>
                  <span className="tp-pal-catcount">{group.items.length}</span>
                  <span className={cn('tp-pal-chev', open && 'is-open')}>▾</span>
                </button>
                {open ? (
                  <div className="tp-pal-items">
                    {group.items.map((item) => {
                      const chips = paramChips(item, t);
                      return (
                        <div
                          key={item.id}
                          className="tp-pal-item"
                          draggable={!readOnly}
                          onDragStart={(event) => handleDragStart(event, item)}
                          onDragEnd={onItemDragEnd}
                          title={itemName(item)}
                        >
                          <span className="tp-pal-itemicon">
                            <TypeIcon
                              type={item.type}
                              transportKind={item.transportKind ?? null}
                              size={12}
                            />
                          </span>
                          <span className="tp-pal-itemtext">
                            <span className="tp-pal-itemname">{itemName(item)}</span>
                            <span className="tp-pal-itemmeta">
                              {item.subcategoryName ? (
                                <span className="tp-pal-subcat">{item.subcategoryName}</span>
                              ) : null}
                              {chips.map((chip, index) => (
                                <span
                                  key={index}
                                  className="rounded-phx-sm border-phx-border bg-phx-surface-2 text-phx-xs text-phx-text-2 inline-flex items-center border px-1.5 py-px whitespace-nowrap"
                                >
                                  {chip}
                                </span>
                              ))}
                            </span>
                            {item.destinationName || item.code ? (
                              <span className="tp-pal-itemfoot">
                                {item.destinationName ? (
                                  <span className="text-phx-text-muted gap-phx-1_5 inline-flex items-center">
                                    <MapPin size={9} />
                                    {item.destinationName}
                                  </span>
                                ) : null}
                                {item.code ? (
                                  <span className="tp-pal-code">{item.code}</span>
                                ) : null}
                              </span>
                            ) : null}
                          </span>
                        </div>
                      );
                    })}
                    <div
                      className={cn(
                        'gap-phx-2 rounded-phx-md border-phx-border mx-3.5 mt-0.5 mb-1 flex items-center border border-dashed px-2 py-1.5',
                        readOnly
                          ? 'cursor-default opacity-60'
                          : 'hover:border-phx-primary hover:bg-phx-surface-2 cursor-grab'
                      )}
                      draggable={!readOnly}
                      onDragStart={(event) => handleBlankDragStart(event, group)}
                      onDragEnd={onItemDragEnd}
                      title={t('costScheme.palette.blankBox', { group: group.label })}
                    >
                      <span className="rounded-phx-sm border-phx-border text-phx-text-muted flex h-4 w-4 flex-none items-center justify-center border border-dashed">
                        <Plus size={11} />
                      </span>
                      <span className="flex min-w-0 flex-col">
                        <span className="text-phx-xs text-phx-text-2 truncate font-semibold">
                          {t('costScheme.palette.blankBox', { group: group.label })}
                        </span>
                        <span className="text-phx-text-muted text-phx-2xs truncate">
                          {t('costScheme.palette.blankBoxSub')}
                        </span>
                      </span>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </aside>
  );
}
